import { parse } from 'date-fns'

import { useState, useEffect } from 'react'

import cn from 'classnames'

import { CountdownTimer, STATUS } from '../../../../components/ui/countdown-timer'
import { NetworkLabel } from '../../../../components/ui/network-label'
import {
  ETransactionStatuses,
  ETransactionTypes,
  ITransaction,
  TransactionAccountType,
} from '../../../../store/fetchers/users/types'
import { useAppSelector } from '../../../../store/hooks'
import { getCurrencies } from '../../../../store/reducers/currencies'
import { formatNumber } from '../../../../utils/formatters'
import { truncateWord } from '../../../../utils/truncate-address'
import { useCurrency } from '../../../../utils/use-currency'

import Tooltip from '../../../../components/global/tooltip'

import {
  TransactionTypeView,
  TransactionStatusView,
} from '../../../../components/ui/transaction-history/components/transaction/enum'

import { IAmountAndCoin } from '../../wallets/info/components/tab-transactions'

import { floorNumber } from '../../../../utils/floor-number'

import CancelTransaction from '../../loans/info/loan-info/components/loan/_type/_active/components/operation-item/cancel-transaction'

import DeleteIcon from './images/Delete Icon.svg'

import Exchange from './images/exchange.svg'
import Interest from './images/interest.svg'
import Loan from './images/loan.svg'
import Redemption from './images/redemption.svg'
import Repayment from './images/repayment.svg'
import TopUp from './images/top-up.svg'
import Withdrawal from './images/withdrawal.svg'
import InterestPercent from './images/interest_percent.svg'
import MonthlyInterest from './images/monthly_interest.svg'

import { DashboardOperationItemProps } from './operation-item.props'
import styles from './styles.module.scss'
import { isIncomingTransaction, isPositiveTransaction } from './utils/is-incoming-transaction'
import { useTranslation } from 'utils/use-translation'
import { CopyButtonWithSuccess } from 'components/ui/copy-button-with-success'

export const matchType = (type: ETransactionTypes, accountType: TransactionAccountType): TransactionTypeView => {
  if (type === ETransactionTypes.DEPOSIT && (accountType === 'loan' || accountType === 'saving')) {
    return TransactionTypeView.DEPOSIT
  }

  if (type === ETransactionTypes.DEPOSIT && accountType === 'wallet') {
    return TransactionTypeView.TOP_UP
  }

  if (type === ETransactionTypes.REPAYMENT) {
    return TransactionTypeView.REPAYMENT
  }

  if (type === ETransactionTypes.INTEREST) {
    return TransactionTypeView.INTEREST
  }

  if (type === ETransactionTypes.WITHDRAW && accountType === 'loan') {
    return TransactionTypeView.LOAN
  }

  if (type === ETransactionTypes.WITHDRAW && (accountType === 'wallet' || accountType === 'saving')) {
    return TransactionTypeView.WITHDRAWAL
  }

  if (type === ETransactionTypes.PLEDGE) {
    return TransactionTypeView.REDEMPTION
  }

  if (type === ETransactionTypes.EXCHANGE) {
    return TransactionTypeView.EXCHANGE
  }

  if (type === ETransactionTypes.COLLATERAL) {
    return TransactionTypeView.COLLATERAL
  }

  if (type === ETransactionTypes.LOAN) {
    return TransactionTypeView.LOAN
  }

  if (type === ETransactionTypes.MONTHLY_INTEREST || type === ETransactionTypes.HOURLY_INTEREST) {
    return TransactionTypeView.MONTHLY_INTEREST
  }

  return null
}

export const matchStatus = (status: ETransactionStatuses): TransactionStatusView => {
  switch (status?.toLowerCase()) {
    case ETransactionStatuses.PENDING:
      return TransactionStatusView.PENDING
    case ETransactionStatuses.CONFIRMING:
      return TransactionStatusView.CONFIRMING
    case ETransactionStatuses.PROCESSING:
    case ETransactionStatuses.REJECTED:
      return TransactionStatusView.PROCESSING
    case ETransactionStatuses.FINISHED:
      return TransactionStatusView.SUCCESS
    case ETransactionStatuses.REFUNDED:
      return TransactionStatusView.REFUNDED
    default:
      return null
  }
}

export const getTypeText = (type: TransactionTypeView) => {
  switch (type) {
    case TransactionTypeView.LOAN_EVENT:
    case TransactionTypeView.MONTHLY_INTEREST:
    case TransactionTypeView.HOURLY_INTEREST:
      return 'Interest'
    case TransactionTypeView.TOP_UP:
      return 'top_up'
    default:
      return type
  }
}

export const getImage = (type: TransactionTypeView) => {
  switch (type) {
    case TransactionTypeView.EXCHANGE:
      return Exchange
    case TransactionTypeView.INTEREST:
      return Interest
    case TransactionTypeView.LOAN:
      return Loan
    case TransactionTypeView.REDEMPTION:
    case TransactionTypeView.COLLATERAL:
      return Redemption
    case TransactionTypeView.REPAYMENT:
      return Repayment
    case TransactionTypeView.DEPOSIT:
    case TransactionTypeView.INCREASE:
    case TransactionTypeView.TOP_UP:
      return TopUp
    case TransactionTypeView.LOAN_EVENT:
      return InterestPercent
    case TransactionTypeView.WITHDRAWAL:
      return Withdrawal
    case TransactionTypeView.MONTHLY_INTEREST:
    case TransactionTypeView.HOURLY_INTEREST:
      return MonthlyInterest
    default:
      return ''
  }
}

export const matchAmountAndCoin = (tx: ITransaction): IAmountAndCoin => {
  if (tx.transaction_type === ETransactionTypes.DEPOSIT && tx.account_type !== 'saving') {
    return {
      amount: tx.to_amount && Number(tx.to_amount) !== 0 ? tx.to_amount : tx.from_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.DEPOSIT && tx.account_type === 'saving') {
    return {
      amount: tx.to_amount && Number(tx.to_amount) !== 0 ? tx.to_amount : tx.from_amount,
      code: tx.from_currency,
      network: tx.from_network,
      incoming: true,
    }
  }

  if (
    tx.transaction_type === ETransactionTypes.REPAYMENT &&
    (tx.account_type === 'loan' || tx.account_type === 'saving')
  ) {
    return {
      amount: tx.to_amount && Number(tx.to_amount) !== 0 ? tx.to_amount : tx.from_amount,
      code: tx.to_amount ? tx.to_currency : tx.from_currency,
      network: tx.to_amount ? tx.to_network : tx.from_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.REPAYMENT && tx.account_type === 'wallet') {
    return {
      amount: tx.from_amount,
      code: tx.from_currency,
      network: tx.from_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'loan') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'saving') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.WITHDRAW && tx.account_type === 'loan') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.WITHDRAW && tx.account_type === 'wallet') {
    return {
      amount: tx.from_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'saving') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.PLEDGE) {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  return {
    amount: tx.from_amount,
    code: tx.from_currency,
    network: tx.from_network,
    incoming: true,
  }
}

export default function DashboardOperationItem({ transaction }: DashboardOperationItemProps) {
  const { t } = useTranslation()
  
  const currencies = useAppSelector(getCurrencies)
  const [screenWidth, setScreenWidth] = useState<number>(null)
  const [isIncoming, setIsIncoming] = useState<boolean>(false)
  const [isPositive, setIsPositive] = useState<boolean>(false)
  const [isCancelTx, setIsCancelTx] = useState<boolean>(false)

  const currency = useCurrency(
    {
      code: matchAmountAndCoin(transaction).code,
      network: matchAmountAndCoin(transaction).network,
    },
    currencies,
  )

  useEffect(() => {
    if (transaction) {
      setIsIncoming(isIncomingTransaction(transaction))
      setIsPositive(isPositiveTransaction(transaction))
    }
  }, [transaction])

  useEffect(() => {
    setScreenWidth(window.innerWidth)
  }, [])

  const formatDate = () => {
    // Преобразуем строку даты в более совместимый формат
    const formattedDateString = transaction.created_at.replace(' ', 'T').replace(' +00:00', 'Z')

    // Преобразуем строку даты в объект Date
    const date = parse(formattedDateString, "yyyy-MM-dd'T'HH:mm:ss.SSSX", new Date())

    return new Date(date.toUTCString())
  }

  return screenWidth > 840 ? (
    <div className={styles.operationItem}>
      <div className={styles.operationItem__coin}>
        <img src={currency?.logo_url} alt="coin logo" />
        <div className={styles.content}>
          <div className={styles.title}>
            <h2>{currency?.code?.toUpperCase()}</h2>
            {currency && <NetworkLabel className={styles.network} currency={currency} />}
          </div>

          <div className={styles.name}>
            <p>{currency?.name}</p>
          </div>
        </div>
      </div>

      <div className={styles.operationItem__type}>
        <img src={getImage(matchType(transaction?.transaction_type, transaction?.account_type))} alt="" />
        <p>{t(getTypeText(matchType(transaction?.transaction_type, transaction?.account_type))?.toLowerCase())}</p>
      </div>

      <div
        className={cn(
          styles.operationItem__amount,
          { [styles.positive]: isPositive },
          { [styles.negative]: !isPositive },
        )}
      >
        <p className={styles.operationItem__amount__text}>
          {isPositive ? '+ ' : '- '}
          {formatNumber(
            isIncoming ? floorNumber(+transaction?.from_amount, 6) : floorNumber(+transaction?.to_amount, 6),
          )?.length > 10
            ? formatNumber(floorNumber(+matchAmountAndCoin(transaction).amount, 6)).slice(0, 10) + '...'
            : formatNumber(floorNumber(+matchAmountAndCoin(transaction).amount, 6))}{' '}
          {currency?.code?.toUpperCase()}
        </p>

        {('+ ' + formatNumber(floorNumber(+matchAmountAndCoin(transaction).amount, 6)) + currency?.code?.toUpperCase())
          ?.length > 24 && (
          <Tooltip className={styles.tooltip}>
            <p className={styles.tooltip__text}>
              {isPositive ? '+ ' : '- '}
              {formatNumber(floorNumber(+matchAmountAndCoin(transaction).amount, 6))} {currency?.code?.toUpperCase()}
            </p>
          </Tooltip>
        )}
      </div>

      <div className={styles.operationItem__data}>
        {(transaction.from_address || transaction.to_address) && (
          <div className={styles.data}>
            {(transaction.from_address || transaction.to_address) && (transaction?.to_address !== 'internal_wallet') &&
              <p>{truncateWord(isIncoming ? transaction.from_address : transaction.to_address, 6, 6)}</p>}
            {(transaction.from_address || transaction.to_address) && (transaction?.to_address !== 'internal_wallet') && (
              <CopyButtonWithSuccess
                value={isIncoming ? transaction.from_address : transaction.to_address}
                className={styles.copy}
              />
            )}
          </div>
        )}

        {(transaction.from_extra_id || transaction.to_extra_id) && (
          <div className={styles.data}>
            <p>
              {isIncoming ? truncateWord(transaction.from_extra_id, 4, 4) : truncateWord(transaction.to_extra_id, 4, 4)}
            </p>
            <CopyButtonWithSuccess
              value={isIncoming ? transaction.from_extra_id : transaction.to_extra_id}
              className={styles.copy}
            />
          </div>
        )}
      </div>

      <div className={styles.operationItem__status}>
        <div className={styles.status}>
          <p
            className={
              styles[
                transaction?.status?.toLowerCase() === 'rejected' ? 'processing' : transaction?.status?.toLowerCase()
              ]
            }
          >
            {transaction?.status?.toLowerCase() === 'rejected' ? t('processing') : t(transaction?.status?.toLowerCase())}
          </p>

          {transaction.status === ETransactionStatuses.PENDING && (
            <CountdownTimer type={STATUS} creationDate={formatDate()} />
          )}
        </div>

        <p className={styles.id}>
          ID: {transaction.transaction_id}{' '}
          <CopyButtonWithSuccess 
            value={transaction.transaction_id} 
            className={styles.copy} />
        </p>
      </div>

      {transaction.status === ETransactionStatuses.PENDING && (
        <button onClick={() => setIsCancelTx(true)} className={styles.cancelTx}>
          <img src={DeleteIcon} alt="cancel transaction" />
        </button>
      )}

      {isCancelTx && <CancelTransaction transaction={transaction} currency={currency} setIsOpened={setIsCancelTx} />}
    </div>
  ) : (
    <div className={styles.operationItem_mobile}>
      <div className={styles.titles}>
        <p>{t('asset_name')}</p>
        <p>{t(getTypeText(matchType(transaction.transaction_type, transaction.account_type))?.toLowerCase())}</p>
        {(transaction.to_address || transaction.from_address) && <p>{t('address')}</p>}
        {(transaction.from_extra_id || transaction.to_extra_id) && <p>{t('tag')}</p>}
        <p>{t('status')}</p>
      </div>

      <div className={styles.line}></div>

      <div className={styles.main}>
        <div className={styles.coin}>
          <img src={currency?.logo_url} alt="coin icon" />
          <div className={styles.content}>
            <h2>{currency?.code?.toUpperCase()}</h2>
            <p>{currency?.name}</p>
            {currency && <NetworkLabel className={styles.network} currency={currency} />}
          </div>
        </div>

        <p
          className={cn(
            styles.receive,
            { [styles.receive_positive]: isPositive },
            { [styles.receive_negative]: !isPositive },
          )}
        >
          {isPositive ? '+ ' : '- '}
          {formatNumber(floorNumber(+matchAmountAndCoin(transaction).amount, 6))} {currency?.code?.toUpperCase()}
        </p>

          {(transaction.from_address || transaction.to_address) && <div className={styles.textCopy}>
            {(transaction?.to_address !== 'internal_wallet' 
                ? <>
                  <p>{truncateWord(isIncoming ? transaction.from_address : transaction.to_address, 8, 8)}</p>
                  <CopyButtonWithSuccess 
                    value={isIncoming ? transaction.from_address : transaction.to_address} 
                    className={styles.copy}
                    />
                </> 
                : <p>{t('internal_wallet')}</p>)
            }
          </div>}

        {(transaction.from_extra_id || transaction.to_extra_id) && (
          <div className={styles.textCopy}>
            <p>
              {isIncoming ? truncateWord(transaction.from_extra_id, 4, 4) : truncateWord(transaction.to_extra_id, 4, 4)}
            </p>
            <CopyButtonWithSuccess 
              value={isIncoming ? transaction.from_extra_id : transaction.to_extra_id}
              className={styles.copy}
              />
          </div>
        )}

        <div className={styles.status}>
          <div className={styles.status}>
            <p
              className={
                styles[
                  transaction?.status?.toLowerCase() === 'rejected' ? 'processing' : transaction?.status?.toLowerCase()
                ]
              }
            >
              {transaction?.status?.toLowerCase() === 'rejected' ? t('processing') : t(transaction?.status?.toLowerCase())}
            </p>

            {transaction.status === ETransactionStatuses.PENDING && (
              <CountdownTimer type={STATUS} creationDate={formatDate()} />
            )}
          </div>
        </div>
      </div>

      {transaction.status === ETransactionStatuses.PENDING && (
        <button onClick={() => setIsCancelTx(true)} className={styles.cancelTx}>
          <img src={DeleteIcon} alt="cancel transaction" />
        </button>
      )}

      {isCancelTx && <CancelTransaction transaction={transaction} currency={currency} setIsOpened={setIsCancelTx} />}
    </div>
  )
}
